import type { GetDetailsResponse, GetListResponse } from './common';
import type { Goal } from '../../../models/utils/goal.model';

///////////////////
// MGA
///////////////////
export enum MgaWithGoalsKeys {
  id = 'id',
  agencyPlatformId = 'agencyPlatformId',
  name = 'name',
  type = 'type',
  goals = 'goals',
}

export type MgaWithGoalsResponse = {
  id: number;
  agencyPlatformId: string;
  name: string | null;
  type: string | null;
  goals: MgaSalesGoalsShortResponse[];
  /** Aggregated from `reporting.agent_portal_paid`; see the list item's note. */
  production: MgaProductionByYearResponse[];
};

export type MgaWithGoalsDetailsResponse = GetDetailsResponse<MgaWithGoalsResponse>;

/** Weighted premium for one year, as returned alongside an agency's goals. */
export type MgaProductionByYearResponse = {
  year: number;
  premiumTotal: number;
};

export type MgaWithGoalsListItemResponse = Pick<MgaWithGoalsResponse, 'id' | 'agencyPlatformId' | 'name' | 'type'> & {
  [key: `year_${number}_goal`]: number | null;
  [key: `year_${number}_production`]: number | null;
};

export type MgaWithGoalsGetListResponse = GetListResponse<MgaWithGoalsListItemResponse> & {
  summary?: any[] | null; // TODO: Define a proper type for the summary array.
};

///////////////////
// Placeholders
///////////////////
export type PlaceholderWithGoalsListItemResponse = {
  id: number;
  name: string;
  [key: `year_${number}_goal`]: number | null;
};
export type PlaceholderWithGoalsGetListResponse = GetListResponse<PlaceholderWithGoalsListItemResponse> & {
  summary?: any[] | null;
};

export type PlaceholderWithGoalsResponse = {
  id: number;
  name: string;
  goals: MgaSalesGoalsShortResponse[];
};

export type PlaceholderWithGoalsDetailsResponse = GetDetailsResponse<PlaceholderWithGoalsResponse>;

///////////////////
// Goals
///////////////////
export type MgaSalesGoalsShortResponse = Pick<MgaSalesGoalsResponse, 'year' | 'amount'>;

type MgaSalesGoalsResponseCommon = {
  id: number;
  year: number;
  amount: number;
  createdAt: Date;
  createdByName: string | null;
  createdByEmail: string | null;
  createdByAgentId: string;
  updatedAt: Date;
  updatedByName: string | null;
  updatedByEmail: string | null;
  updatedByAgentId: string;
};

export type MgaSalesGoalsResponse = { agencyId: number } & MgaSalesGoalsResponseCommon;
export type PlaceholderSalesGoalsResponse = { placeholderId: number } & MgaSalesGoalsResponseCommon;

export type MgaSalesGoalsDetailsResponse = GetDetailsResponse<MgaSalesGoalsResponse>;
export type PlaceholderSalesGoalsDetailsResponse = GetDetailsResponse<PlaceholderSalesGoalsResponse>;

/** Still read by ag-common-svc's MgaSalesGoalsApiService. */
export type MgaSalesGoalsByAgencyResponse = GetDetailsResponse<Goal[]>;

///////////////////
// Comments
///////////////////
export enum MgaSalesGoalCommentKeys {
  id = 'id',
  goalId = 'goalId',
  year = 'year',
  amount = 'amount',
  comment = 'comment',
  commentedAt = 'commentedAt',
  commentedByName = 'commentedByName',
  commentedByEmail = 'commentedByEmail',
  commentedByAgentId = 'commentedByAgentId',
  createdAt = 'createdAt',
  createdByName = 'createdByName',
  createdByEmail = 'createdByEmail',
  createdByAgentId = 'createdByAgentId',
  updatedAt = 'updatedAt',
  updatedByName = 'updatedByName',
  updatedByEmail = 'updatedByEmail',
  updatedByAgentId = 'updatedByAgentId',
}

export type MgaSalesGoalCommentResponse = {
  id: number;
  goalId: number;
  comment: string;
  commentedAt: Date | string;
  commentedByName: string | null;
  commentedByEmail: string | null;
  commentedByAgentId: string;
  createdAt: Date | string;
  createdByName: string | null;
  createdByEmail: string | null;
  createdByAgentId: string;
  updatedAt: Date | string;
  updatedByName: string | null;
  updatedByEmail: string | null;
  updatedByAgentId: string;
};

export type MgaSalesGoalCommentDetailsResponse = GetDetailsResponse<MgaSalesGoalCommentResponse>;

export type MgaSalesGoalCommentListItemResponse = MgaSalesGoalCommentResponse & MgaSalesGoalsShortResponse;

export type MgaSalesGoalCommentGetListResponse = GetListResponse<MgaSalesGoalCommentListItemResponse>;

///////////////////
// Placeholders
///////////////////
export type MgaSalesGoalPlaceholderResponse = {
  id: number;
  name: string;
};
export type MgaSalesGoalPlaceholderDetailsResponse = GetDetailsResponse<MgaSalesGoalPlaceholderResponse>;
